import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Row,
  Col,
  Statistic,
  Modal,
  Input,
  Spin,
  Layout,
  Select,
  DatePicker,
  Badge,
} from "antd";
import dayjs from "dayjs";
import { postAPI, displayMessage } from "../../utils/common";
import {
  ADMIN_PENDING_DISCOUNTS,
  BILL_ACTION_DISCOUNT,
} from "../../constants/api";
import { ERROR_MSG_TYPE } from "../../constants/messages";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

const { RangePicker } = DatePicker;
const { Option } = Select;

function AdminDiscountDashboard() {
  const { Content } = Layout;

  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedBillId, setSelectedBillId] = useState(null);

  /* ================= FETCH ================= */

  const fetchPending = () => {
    setLoading(true);

    postAPI(
      ADMIN_PENDING_DISCOUNTS,
      {},
      (res) => {
        if (res.status) {
          setBills(res.data);
          setFilteredBills(res.data);

          // extract unique vendors
          const uniqueVendors = [
            ...new Map(
              res.data.map((item) => [item.vendor?.id, item.vendor]),
            ).values(),
          ];
          setVendors(uniqueVendors);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, []);

  /* ================= FILTER ================= */

  useEffect(() => {
    let data = [...bills];

    if (selectedVendor) {
      data = data.filter((b) => b.vendor?.id === selectedVendor);
    }

    if (dateRange) {
      data = data.filter((b) => {
        const date = dayjs(b.updated_at);
        return date.isAfter(dateRange[0]) && date.isBefore(dateRange[1]);
      });
    }

    setFilteredBills(data);
  }, [selectedVendor, dateRange, bills]);

  /* ================= APPROVE / REJECT ================= */

  const handleApprove = (billId) => {
    postAPI(
      BILL_ACTION_DISCOUNT,
      { bill_id: billId, action: "approve" },
      (res) => {
        if (res.status) {
          displayMessage("success", res.message);
          setBills((prev) => prev.filter((b) => b.id !== billId));
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
      },
    );
  };

  const handleReject = () => {
    if (!rejectReason) {
      displayMessage(ERROR_MSG_TYPE, "Enter rejection reason");
      return;
    }

    postAPI(
      BILL_ACTION_DISCOUNT,
      {
        bill_id: selectedBillId,
        action: "reject",
        remark: rejectReason,
      },
      (res) => {
        if (res.status) {
          displayMessage("success", res.message);
          setBills((prev) => prev.filter((b) => b.id !== selectedBillId));
          setRejectModalVisible(false);
          setRejectReason("");
        }
      },
    );
  };

  /* ================= CALCULATIONS ================= */

  const totalPendingAmount = filteredBills.reduce(
    (sum, b) => sum + parseFloat(b.discount_amount || 0),
    0,
  );

  /* ================= DISCOUNT % ================= */

  const calculateDiscountPercent = (bill) => {
    const total = parseFloat(bill.total_amount || 0);
    const discount = parseFloat(bill.discount_amount || 0);

    if (!total) return "0.00";

    return ((discount / total) * 100).toFixed(2);
  };

  /* ================= TABLE ================= */

  const columns = [
    {
      title: "Vendor",
      render: (_, row) => row.vendor?.vendor_name,
    },
    {
      title: "Hotel",
      render: (_, row) => row.vendor?.hotel_name,
    },
    { title: "Bill No", dataIndex: "bill_no" },
    { title: "Original Amount", dataIndex: "total_amount" },
    { title: "Discount Amount", dataIndex: "discount_amount" },
    {
      title: "Discount %",
      render: (_, row) => (
        <Tag color="purple">{calculateDiscountPercent(row)}%</Tag>
      ),
    },
    {
      title: "Action",
      render: (_, row) => (
        <Space>
          <Button type="primary" onClick={() => handleApprove(row.id)}>
            Approve
          </Button>
          <Button
            danger
            onClick={() => {
              setSelectedBillId(row.id);
              setRejectModalVisible(true);
            }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  /* ================= RETURN ================= */

  return (
    <Layout>
      <LayoutHeader />
      <Content style={{ padding: 20 }}>
        <Spin spinning={loading}>
          <Card
            title={
              <>
                Discount Approval Dashboard
                <Badge
                  count={filteredBills.length}
                  style={{ backgroundColor: "#faad14" }}
                />
              </>
            }
          >
            {/* ===== Filters ===== */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
              <Col span={6}>
                <Select
                  allowClear
                  placeholder="Filter by Vendor"
                  style={{ width: "100%" }}
                  onChange={(val) => setSelectedVendor(val)}
                >
                  {vendors.map((v) => (
                    <Option key={v.id} value={v.id}>
                      {v.vendor_name}
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col span={8}>
                <RangePicker
                  style={{ width: "100%" }}
                  onChange={(dates) => setDateRange(dates)}
                />
              </Col>
            </Row>

            {/* ===== Stats ===== */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Pending Requests"
                    value={filteredBills.length}
                  />
                </Card>
              </Col>

              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Discount Amount"
                    value={totalPendingAmount}
                    precision={2}
                  />
                </Card>
              </Col>
            </Row>

            {/* ===== Table ===== */}
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredBills}
              pagination={{ pageSize: 10 }}
            />
          </Card>

          {/* Reject Modal */}
          <Modal
            title="Reject Discount Request"
            open={rejectModalVisible}
            onCancel={() => setRejectModalVisible(false)}
            onOk={handleReject}
          >
            <Input.TextArea
              placeholder="Enter rejection reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Modal>
        </Spin>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default AdminDiscountDashboard;
